import { LaunchpadItem, LaunchpadSubtask, TaskStatus } from './LaunchpadProgress.interface';

export type LaunchpadTask = LaunchpadItem | LaunchpadSubtask;

export const isLaunchpadItemCompleted = (item: LaunchpadItem): boolean =>
    item.subtasks.length > 0
        ? item.subtasks.every((subtask) => subtask.status === TaskStatus.COMPLETED)
        : item.status === TaskStatus.COMPLETED;

export const isLaunchpadItemCancelled = (item: LaunchpadItem): boolean =>
    item.subtasks.length > 0
        ? item.subtasks.every((subtask) => subtask.status === TaskStatus.CANCELLED)
        : item.status === TaskStatus.CANCELLED;

export const isLaunchpadItemBlocked = (item: LaunchpadItem, items: LaunchpadItem[]): boolean => {
    if (item.status === TaskStatus.BLOCKED) {
        return true;
    }

    if (item.isFromApi === false) {
        return true;
    }

    return (item.dependency ?? []).some((depId) => {
        const depItem = items.find((i) => i.id === depId || i.template_id === depId);
        if (depItem) {
            if (depItem.subtasks.length > 0) {
                return depItem.subtasks.some((subtask) => subtask.status !== TaskStatus.COMPLETED);
            }
            return depItem.status !== TaskStatus.COMPLETED;
        }

        // A task may depend on a nested subtask instead of a whole parent task.
        const matchingSubtask = items
            .flatMap((topLevelItem) => topLevelItem.subtasks)
            .find((subtask) => subtask.id === depId || subtask.template_id === depId);
        if (matchingSubtask) {
            return matchingSubtask.status !== TaskStatus.COMPLETED;
        }

        return true;
    });
};

/** The task a CTA acts on: the item itself, or its first incomplete subtask. */
export const getNextLaunchpadTask = (item?: LaunchpadItem): LaunchpadTask | null => {
    if (!item) {
        return null;
    }
    if (!item.subtasks.length) {
        return item;
    }
    return item.subtasks.find((subtask) => subtask.status !== TaskStatus.COMPLETED) ?? null;
};

export const isLaunchpadItem = (task: LaunchpadTask): task is LaunchpadItem => 'subtasks' in task;

/** Custom labels win; otherwise in-progress work reads as Continue. */
export const getLaunchpadCtaLabel = (
    task: LaunchpadTask | null,
    item: LaunchpadItem,
    isBlocked: boolean,
    fallbackLabel = 'Start'
): string => {
    if (!task) {
        return fallbackLabel;
    }
    if (task.cta_label) {
        return task.cta_label;
    }
    if (item.cta_label) {
        return item.cta_label;
    }
    if (!isBlocked && task.status === TaskStatus.IN_PROGRESS) {
        return 'Continue';
    }
    return fallbackLabel;
};
